import { clampRgba, isRgba } from '../color.ts';
import { createSkiffBoat, SKIFF_BOAT_ID, type BoatDocument } from '../boat/index.ts';
import type { HighScoreBoat } from './types.ts';

type ScoreBoatSource = Pick<
  BoatDocument,
  'name' | 'lengthM' | 'beamM' | 'turnM' | 'accelM' | 'brakeM' | 'planingSpeedKmh' | 'hull' | 'deck' | 'accent'
>;

export function highScoreBoatFromDocument(id: string, boat: ScoreBoatSource): HighScoreBoat {
  return {
    id,
    name: boat.name,
    lengthM: boat.lengthM,
    beamM: boat.beamM,
    turnM: boat.turnM,
    accelM: boat.accelM,
    brakeM: boat.brakeM,
    planingSpeedKmh: boat.planingSpeedKmh,
    hull: { ...boat.hull },
    deck: { ...boat.deck },
    accent: { ...boat.accent },
  };
}

/** The hard-coded hull every run used before a catalog boat was stored on the row. */
export function skiffHighScoreBoat(): HighScoreBoat {
  return highScoreBoatFromDocument(SKIFF_BOAT_ID, createSkiffBoat());
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Missing or partial hulls become the factory Skiff. A present v5 hull is kept. */
export function migrateHighScoreBoat(value: unknown): HighScoreBoat {
  const skiff = skiffHighScoreBoat();

  if (!isRecord(value)) {
    return skiff;
  }

  const id = typeof value.id === 'string' && value.id.trim() ? value.id : skiff.id;
  const name = typeof value.name === 'string' ? value.name : skiff.name;

  return {
    id,
    name,
    lengthM: asFiniteNumber(value.lengthM) ?? skiff.lengthM,
    beamM: asFiniteNumber(value.beamM) ?? skiff.beamM,
    turnM: asFiniteNumber(value.turnM) ?? skiff.turnM,
    accelM: asFiniteNumber(value.accelM) ?? skiff.accelM,
    brakeM: asFiniteNumber(value.brakeM) ?? skiff.brakeM,
    planingSpeedKmh: asFiniteNumber(value.planingSpeedKmh) ?? skiff.planingSpeedKmh,
    hull: isRgba(value.hull) ? clampRgba(value.hull) : skiff.hull,
    deck: isRgba(value.deck) ? clampRgba(value.deck) : skiff.deck,
    accent: isRgba(value.accent) ? clampRgba(value.accent) : skiff.accent,
  };
}
